
import { useState, useCallback } from "react";
import { Check, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

export interface SelectOption {
  value: string;
  label: string;
}

export interface InlineSelectFieldProps {
  label: string;
  value?: string | null;
  options: SelectOption[];
  onSave: (value: string) => void;
  icon?: React.ReactNode;
  emptyText?: string;
  placeholder?: string;
  className?: string;
  /** Render the display value with custom styling (e.g. a Badge) */
  renderValue?: (option: SelectOption | undefined) => React.ReactNode;
}

export function InlineSelectField({
  label,
  value,
  options,
  onSave,
  icon,
  emptyText,
  placeholder,
  className,
  renderValue,
}: InlineSelectFieldProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = useCallback(
    (next: string) => {
      setOpen(false);
      if (next !== (value ?? "")) {
        onSave(next);
      }
    },
    [value, onSave]
  );

  return (
    <div className={cn("group/field flex items-start gap-2.5", className)}>
      {icon && (
        <div className="mt-0.5 shrink-0 text-muted-foreground">{icon}</div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              className="group/val flex items-center gap-1.5 w-full text-left cursor-pointer rounded-sm hover:bg-accent/40 -mx-1 px-1 py-0.5 transition-colors"
            >
              {selectedOption ? (
                renderValue ? (
                  <span className="flex-1">{renderValue(selectedOption)}</span>
                ) : (
                  <span className="text-sm flex-1 truncate">{selectedOption.label}</span>
                )
              ) : (
                <span className="text-sm text-muted-foreground/50 italic flex-1">
                  {emptyText ?? placeholder ?? `Select ${label.toLowerCase()}…`}
                </span>
              )}
              <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground/30 md:text-transparent md:group-hover/field:text-muted-foreground/40 transition-colors" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="start">
            <Command>
              {options.length > 6 && (
                <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
              )}
              <CommandList>
                <CommandEmpty>No results.</CommandEmpty>
                <CommandGroup>
                  {placeholder && (
                    <CommandItem
                      key="__clear__"
                      value=""
                      onSelect={() => handleSelect("")}
                      className="text-muted-foreground italic"
                    >
                      <Check className={cn("mr-2 h-4 w-4", value ? "opacity-0" : "opacity-100")} />
                      {placeholder}
                    </CommandItem>
                  )}
                  {options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => handleSelect(opt.value)}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value === opt.value ? "opacity-100" : "opacity-0")} />
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
